import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bank, AppWindow, Desktop, DeviceMobile, Globe, MicrosoftExcelLogo,
  Envelope, ChartBar, Wrench, ArrowSquareOut, Package,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { launchApplication } from "@/lib/applicationLauncher";
import { useBranding } from "@/lib/branding";
import ApplicationLaunchDialog from "@/components/ApplicationLaunchDialog";
import WidgetHeaderMeta from "@/components/WidgetHeaderMeta";

/**
 * "My Applications" — dashboard widget backed by the Application Registry.
 *
 * The Version 2.0 unified workspace foundation. Renders every application the
 * caller is explicitly permitted to see (strict allow-list — Administrator
 * does NOT auto-see every app; the registry is the single source of truth).
 *
 * Interaction: the WHOLE card is clickable. All launches route through the
 * centralised Application Launch Service (`lib/applicationLauncher.js`) so
 * adding new launch backends (e.g. the future Coolman Operations Launcher for
 * desktop apps) is a one-file change with zero UI churn.
 *
 * DSG-001 compliant — semantic tokens throughout.
 */

// Keep this map minimal; unknown icons fall back to AppWindow. The admin UI
// exposes these as choices when editing an application.
const ICON_MAP = {
  bank: Bank,
  desktop: Desktop,
  mobile: DeviceMobile,
  web: Globe,
  spreadsheet: MicrosoftExcelLogo,
  envelope: Envelope,
  chart: ChartBar,
  wrench: Wrench,
  package: Package,
  "app-window": AppWindow,
};

const TYPE_ICON = {
  "Desktop Application": Desktop,
  "Mobile Application": DeviceMobile,
  "Web Application": Globe,
  "Native Web Module": AppWindow,
  "External Link": ArrowSquareOut,
};

const STATUS_TONE = {
  "Planned": "border-status-warning-bg bg-status-warning-bg text-status-warning-fg",
  "Pilot":   "border-status-info-bg bg-status-info-bg text-status-info-fg",
  "Active":  "border-status-success-bg bg-status-success-bg text-status-success-fg",
  "Retired": "border-divider-strong bg-surface-alt text-ink-secondary",
};

// Deployment badges — WHERE the application runs. Rendered as a
// secondary chip alongside the primary Status badge on every card.
const DEPLOYMENT_META = {
  Cloud:    { label: "Cloud",    icon: Globe,    tone: "border-divider bg-surface text-ink-secondary" },
  Desktop:  { label: "Desktop",  icon: Desktop,  tone: "border-divider bg-surface text-ink-secondary" },
  Hybrid:   { label: "Hybrid",   icon: Package,  tone: "border-divider bg-surface text-ink-secondary" },
  External: { label: "External", icon: ArrowSquareOut, tone: "border-divider bg-surface text-ink-secondary" },
};

function iconFor(app) {
  return ICON_MAP[app.icon] || TYPE_ICON[app.app_type] || AppWindow;
}

export default function MyApplicationsWidget() {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogPayload, setDialogPayload] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/applications/mine");
      setApps(r.data || []);
      setLastUpdated(new Date().toISOString());
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load applications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      if (alive) await load();
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLaunch = (app) => {
    launchApplication(app, {
      navigate,
      onDialog: (payload) => setDialogPayload(payload),
    });
  };

  return (
    <>
      <div data-testid="my-applications-widget" className="rounded-md border border-divider bg-surface">
        <div className="flex items-center justify-between border-b border-divider px-4 py-2.5">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-ink-secondary">
            My Applications
          </h3>
          <div className="flex items-center gap-2">
            {!loading && apps.length > 0 && (
              <span
                data-testid="my-applications-count"
                className="text-xs font-medium text-ink-secondary"
              >
                {apps.length} {apps.length === 1 ? "app" : "apps"}
              </span>
            )}
            <WidgetHeaderMeta
              testid="my-applications"
              lastUpdated={lastUpdated}
              loading={loading}
              onRefresh={load}
            />
          </div>
        </div>

        <div className="p-4">
          {loading && (
            <div className="py-6 text-center text-sm text-ink-secondary">Loading…</div>
          )}
          {error && !loading && (
            <div data-testid="my-applications-error" className="rounded-md border border-status-error-bg bg-status-error-bg p-3 text-sm text-status-error-fg">
              {error}
            </div>
          )}
          {!loading && !error && apps.length === 0 && (
            <div
              data-testid="my-applications-empty"
              className="rounded-md border border-divider bg-surface-alt p-6 text-center text-sm text-ink-secondary"
            >
              You do not have access to any registered applications yet. Ask an
              administrator to grant you permission in Administration &gt; Applications.
            </div>
          )}
          {!loading && !error && apps.length > 0 && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {apps.map((app) => (
                <AppCard key={app.id} app={app} onLaunch={handleLaunch} />
              ))}
            </div>
          )}
        </div>
      </div>

      <ApplicationLaunchDialog
        payload={dialogPayload}
        onClose={() => setDialogPayload(null)}
      />
    </>
  );
}

function AppCard({ app, onLaunch }) {
  const Icon = iconFor(app);
  const { branding, applicationIconSrc } = useBranding();
  const statusClass = STATUS_TONE[app.status] || "border-divider-strong bg-surface-alt text-ink-secondary";
  const isExternal = app.launch_method === "external_url";
  // DSG-004 Application Branding Inheritance:
  // priority = per-app override URL > global Default Application Icon > Phosphor icon fallback
  // Support both `custom_branding` (Pydantic model shape from admin CRUD) and
  // `branding_overrides` (legacy shape read by /api/applications/{id}/branding).
  const overrideIcon =
    app.custom_branding?.application_icon_url ||
    app.branding_overrides?.application_icon_url;
  const defaultAppIcon = branding?.assets?.application_icon
    ? applicationIconSrc()
    : null;
  const iconImgSrc = overrideIcon || defaultAppIcon;

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onLaunch(app);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Launch ${app.name}`}
      data-testid={`my-application-card-${app.name.replace(/\s+/g, "-").toLowerCase()}`}
      onClick={() => onLaunch(app)}
      onKeyDown={handleKeyDown}
      className="flex cursor-pointer flex-col rounded-md border border-divider bg-surface-alt p-3 transition hover:-translate-y-0.5 hover:border-brand hover:shadow-token-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/40"
    >
      <div className="flex items-start gap-3">
        <div
          data-testid={`my-application-icon-${app.name.replace(/\s+/g, "-").toLowerCase()}`}
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-md bg-brand text-brand-foreground"
        >
          {iconImgSrc ? (
            <img
              src={iconImgSrc}
              alt=""
              className="h-full w-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <Icon size={20} weight="bold" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1 truncate text-sm font-semibold text-ink-primary" title={app.name}>
            <span className="truncate">{app.name}</span>
            {isExternal && <ArrowSquareOut size={12} weight="bold" className="flex-shrink-0 text-ink-secondary" />}
          </div>
          <div className="mt-0.5 text-xs text-ink-secondary">{app.app_type}</div>
        </div>
      </div>

      {app.description && (
        <p className="mt-2 line-clamp-3 text-xs text-ink-secondary">{app.description}</p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <span
          className={`inline-flex items-center rounded-sm border px-1.5 py-0.5 text-[11px] font-medium ${statusClass}`}
        >
          {app.status}
        </span>
        {(() => {
          const dep = DEPLOYMENT_META[app.deployment];
          if (!dep) return null;
          const DepIcon = dep.icon;
          return (
            <span
              data-testid={`my-application-deployment-${app.deployment.toLowerCase()}`}
              className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[11px] font-medium ${dep.tone}`}
              title={`Deployment · ${dep.label}`}
            >
              <DepIcon size={11} weight="bold" />
              {dep.label}
            </span>
          );
        })()}
        {app.version && (
          <span className="inline-flex items-center rounded-sm border border-divider bg-surface px-1.5 py-0.5 font-mono text-[11px] text-ink-secondary">
            v{app.version}
          </span>
        )}
      </div>

      {app.owner_name && (
        <div className="mt-3 border-t border-divider pt-2 text-[11px] text-ink-secondary" title={app.owner_email || ""}>
          Owner: {app.owner_name}
        </div>
      )}
    </div>
  );
}
